/**
 * lib/dcExport.js
 * "Export DCs" on the Delivery Challans page — CSV, Excel (.xlsx via
 * `xlsx`) and PDF (via `jspdf`) of the same rows: one per DC line, as
 * returned by GET /delivery-challans/export-lines. The PDF groups the rows
 * by DC (header details once, then its lines, a subtotal per DC and a grand
 * total at the end).
 */
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";

export const EXPORT_COLUMNS = [
  { field: "dc_no", header: "DC No" },
  { field: "dc_date", header: "DC Date", kind: "date" },
  { field: "party", header: "Customer / Supplier" },
  { field: "order_no", header: "Transfer Order No / Job No / SO No" },
  { field: "part_no", header: "Part No" },
  { field: "item_description", header: "Item Description" },
  { field: "qty", header: "Qty", kind: "qty" },
  { field: "rate", header: "Rate", kind: "money" },
  { field: "amount", header: "Amount", kind: "money" },
  { field: "delivery_date", header: "Delivery Date", kind: "date" },
  { field: "status", header: "Status" },
  { field: "vehicle", header: "Vehicle" },
  { field: "transporter", header: "Transporter" },
  { field: "driver", header: "Driver" },
  { field: "gin_status", header: "GIN Status" },
];

// "2026-10-05" → "05-10-2026"
export function fmtDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
}

const fmtQty = (v) => String(Math.round(Number(v || 0) * 1000) / 1000);
const fmtMoney = (v) =>
  v == null ? "" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function cellText(row, col) {
  const v = row[col.field];
  if (col.kind === "date") return fmtDate(v);
  if (col.kind === "qty") return fmtQty(v);
  if (col.kind === "money") return fmtMoney(v);
  return v == null ? "" : String(v);
}

// Raw (unformatted) value for CSV / Excel cells — numbers stay numbers.
function cellValue(row, col) {
  const v = row[col.field];
  if (col.kind === "qty") return Number(v || 0);
  if (col.kind === "money") return v == null ? "" : Math.round(Number(v) * 100) / 100;
  return cellText(row, col);
}

// DC-Export-01102026-to-31102026
export function exportFileName(fromDate, toDate) {
  const compact = (iso) => fmtDate(iso).replace(/-/g, "");
  return `DC-Export-${compact(fromDate)}-to-${compact(toDate)}`;
}

const sum = (rows, f) => rows.reduce((a, r) => a + Number(r[f] || 0), 0);

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportDcsCsv(rows, fromDate, toDate) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    EXPORT_COLUMNS.map((c) => esc(c.header)).join(","),
    ...rows.map((r) => EXPORT_COLUMNS.map((c) => esc(cellValue(r, c))).join(",")),
  ];
  // BOM so Excel opens the UTF-8 file with the right encoding.
  download(new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }), `${exportFileName(fromDate, toDate)}.csv`);
}

export function exportDcsExcel(rows, fromDate, toDate) {
  const aoa = [EXPORT_COLUMNS.map((c) => c.header), ...rows.map((r) => EXPORT_COLUMNS.map((c) => cellValue(r, c)))];
  if (rows.length) {
    aoa.push(EXPORT_COLUMNS.map((c, i) => (i === 0 ? "Total" : c.field === "qty" || c.field === "amount" ? Math.round(sum(rows, c.field) * 100) / 100 : "")));
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wide = { party: 28, order_no: 26, item_description: 32, transporter: 18 };
  ws["!cols"] = EXPORT_COLUMNS.map((c) => ({ wch: wide[c.field] || 14 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Delivery Challans");
  XLSX.writeFile(wb, `${exportFileName(fromDate, toDate)}.xlsx`);
}

// Line columns under each DC's header block in the PDF (DC-level fields
// — No, Date, Customer, TO/JO/SO No, Vehicle, Transporter — are in the header).
const PDF_LINE_COLUMNS = [
  { field: "part_no", header: "Part No", w: 90 },
  { field: "item_description", header: "Item Description", w: 0 },
  { field: "qty", header: "Qty", w: 52, kind: "qty", right: true },
  { field: "rate", header: "Rate", w: 70, kind: "money", right: true },
  { field: "amount", header: "Amount", w: 84, kind: "money", right: true },
  { field: "delivery_date", header: "Delivery Date", w: 66, kind: "date" },
  { field: "status", header: "Status", w: 62 },
  { field: "driver", header: "Driver", w: 80 },
  { field: "gin_status", header: "GIN Status", w: 58 },
];

export function exportDcsPdf(rows, fromDate, toDate, statusLabel) {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 28;
  const tableW = pageW - margin * 2;
  const cols = PDF_LINE_COLUMNS.map((c) => ({ ...c }));
  cols[1].w = tableW - cols.reduce((a, c) => a + c.w, 0);
  const lineH = 9;
  const bottom = pageH - 30;
  let y = 40;

  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(15, 23, 42);
  doc.text("Delivery Challans", margin, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(100, 116, 139);
  doc.text(`Generated ${new Date().toLocaleString("en-IN")}`, pageW - margin, y, { align: "right" });
  y += 14;
  doc.text(`DC Date ${fmtDate(fromDate)} to ${fmtDate(toDate)}   ·   Status: ${statusLabel}`, margin, y);
  y += 8;
  doc.setDrawColor(37, 99, 235); doc.setLineWidth(1.2);
  doc.line(margin, y, pageW - margin, y);
  doc.setLineWidth(0.5);
  y += 16;

  const newPage = () => { doc.addPage(); y = 40; };

  const drawColHeader = () => {
    doc.setFillColor(241, 245, 249); doc.setDrawColor(226, 232, 240);
    doc.rect(margin, y, tableW, 16, "FD");
    doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(71, 85, 105);
    let cx = margin;
    cols.forEach((c) => {
      if (c.right) doc.text(c.header, cx + c.w - 4, y + 11, { align: "right" });
      else doc.text(c.header, cx + 4, y + 11);
      cx += c.w;
    });
    y += 16;
  };

  const drawDcHeader = (first, cont) => {
    const fields = [
      ["DC No", first.dc_no + (cont ? " (contd.)" : "")],
      ["Date", fmtDate(first.dc_date)],
      ["Customer / Supplier", first.party || "—"],
      ["TO / JO / SO No", first.order_no || "—"],
      ["Vehicle", first.vehicle || "—"],
      ["Transporter", first.transporter || "—"],
    ];
    const widths = [0.12, 0.09, 0.27, 0.22, 0.13, 0.17].map((f) => f * tableW);
    doc.setFillColor(239, 246, 255); doc.setDrawColor(191, 219, 254);
    doc.rect(margin, y, tableW, 26, "FD");
    let cx = margin;
    fields.forEach(([label, value], i) => {
      doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(100, 116, 139);
      doc.text(label.toUpperCase(), cx + 6, y + 9);
      doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(15, 23, 42);
      doc.text(doc.splitTextToSize(String(value), widths[i] - 10)[0] || "", cx + 6, y + 20);
      cx += widths[i];
    });
    y += 26;
    drawColHeader();
  };

  const drawTotalRow = (label, group, strong) => {
    if (y + 16 > bottom) newPage();
    if (strong) { doc.setFillColor(226, 232, 240); doc.rect(margin, y, tableW, 18, "F"); }
    doc.setFont("helvetica", "bold"); doc.setFontSize(strong ? 8.5 : 7.5); doc.setTextColor(15, 23, 42);
    let cx = margin;
    cols.forEach((c, i) => {
      if (i === 1) doc.text(label, cx + 4, y + 12);
      if (c.field === "qty") doc.text(fmtQty(sum(group, "qty")), cx + c.w - 4, y + 12, { align: "right" });
      if (c.field === "amount") doc.text(fmtMoney(sum(group, "amount")), cx + c.w - 4, y + 12, { align: "right" });
      cx += c.w;
    });
    y += strong ? 18 : 16;
  };

  // Group by DC, keeping the server's order (oldest DC first).
  const groups = [];
  const byDc = new Map();
  rows.forEach((r) => {
    if (!byDc.has(r.dc_id)) { byDc.set(r.dc_id, []); groups.push(byDc.get(r.dc_id)); }
    byDc.get(r.dc_id).push(r);
  });

  if (!groups.length) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(148, 163, 184);
    doc.text("No delivery challans match the selected filters.", margin, y + 10);
  }

  groups.forEach((group) => {
    if (y + 26 + 16 + 20 > bottom) newPage();
    drawDcHeader(group[0], false);
    group.forEach((r, ri) => {
      doc.setFont("helvetica", "normal"); doc.setFontSize(7);
      const cells = cols.map((c) => doc.splitTextToSize(cellText(r, c), c.w - 8));
      const h = Math.max(...cells.map((l) => l.length)) * lineH + 6;
      // Keep the DC's subtotal on the same page as its last line.
      if (y + h + (ri === group.length - 1 ? 16 : 0) > bottom) {
        newPage();
        drawDcHeader(group[0], true);
        doc.setFont("helvetica", "normal"); doc.setFontSize(7);
      }
      doc.setTextColor(15, 23, 42); doc.setDrawColor(226, 232, 240);
      let cx = margin;
      cols.forEach((c, i) => {
        cells[i].forEach((line, li) => {
          if (c.right) doc.text(line, cx + c.w - 4, y + 10 + li * lineH, { align: "right" });
          else doc.text(line, cx + 4, y + 10 + li * lineH);
        });
        cx += c.w;
      });
      y += h;
      doc.line(margin, y, pageW - margin, y);
    });
    drawTotalRow(`Subtotal — ${group[0].dc_no}`, group, false);
    y += 12;
  });

  if (groups.length) drawTotalRow(`Grand Total — ${groups.length} DC(s), ${rows.length} line(s)`, rows, true);

  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
    doc.text(`Page ${p} of ${pages}`, pageW - margin, pageH - 14, { align: "right" });
  }
  doc.save(`${exportFileName(fromDate, toDate)}.pdf`);
}
